<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { ChevronDown, ChevronRight } from "lucide-vue-next";
  import { TableCell, TableRow } from "@/components/ui/table";
  import { Switch } from "@/components/ui/switch";
  import { InputNumber } from "@/components/episto-ui/input-number";
  import { Tooltip } from "@/components/ui/tooltip";
  import { Checkbox } from "@/components/ui/checkbox";
  import { Badge } from "@/components/ui/badge";
  import { useMCampaignStore } from "@stores/mCampaign";
  import { useCAdsetsStore } from "@stores/cAdsets";
  import { useCCampaignsStore } from "@stores/cCampaigns";
  import { useBudgetView } from "@composables/budget/useBudgetView";
  import { useBudgetSelection } from "@composables/budget/useBudgetSelection";

  const props = defineProps({
    ccampaign: { type: Object, required: true },
    totalDaily: { type: Number, default: 0 },
    collapsed: { type: Boolean, default: false },
  });

  defineEmits(["toggle-collapse", "toggle-status"]);

  const { currentMCampaign } = storeToRefs(useMCampaignStore());
  const cAdsetsStore = useCAdsetsStore();
  const { cAdsets } = storeToRefs(cAdsetsStore);
  const { isCAdsetVisible } = cAdsetsStore;
  const { setCCampaignBudgetManual } = useCCampaignsStore();
  const { showConstraints } = useBudgetView();
  const { selectedIds, isSelected, setSelection } = useBudgetSelection();

  const isActive = computed(() => props.ccampaign.status === "active");
  const masterOverrideActive = computed(() => !!currentMCampaign.value?.daily_budget_manual);

  const groupName = computed(() => {
    const mGroup = currentMCampaign.value?.m_groups?.find((g) => g.token === props.ccampaign.group_token);
    return mGroup?.name ?? props.ccampaign.name;
  });

  const visibleAdsetIdsInGroup = computed(() =>
    cAdsets.value
      .filter((a) => a.group_token === props.ccampaign.group_token && isCAdsetVisible(a))
      .map((a) => a.id),
  );

  const allGroupAdsetsSelected = computed(
    () => visibleAdsetIdsInGroup.value.length > 0 && visibleAdsetIdsInGroup.value.every((id) => isSelected(id)),
  );

  const toggleGroupSelection = () => {
    const next = new Set(selectedIds.value);
    if (allGroupAdsetsSelected.value) {
      visibleAdsetIdsInGroup.value.forEach((id) => next.delete(id));
    } else {
      visibleAdsetIdsInGroup.value.forEach((id) => next.add(id));
    }
    setSelection([...next]);
  };
</script>

<template>
  <TableRow class="font-medium">
    <TableCell class="w-10">
      <Checkbox
        :model-value="allGroupAdsetsSelected"
        @update:model-value="toggleGroupSelection" />
    </TableCell>
    <TableCell class="w-14">
      <Switch
        :model-value="isActive"
        @update:model-value="$emit('toggle-status')" />
    </TableCell>
    <TableCell :class="['w-[260px]', showConstraints ? 'sticky left-[6rem] z-10 bg-background' : '']">
      <button
        type="button"
        class="flex items-center gap-2 text-left"
        @click="$emit('toggle-collapse')">
        <component
          :is="collapsed ? ChevronRight : ChevronDown"
          class="h-4 w-4 text-muted-foreground" />
        <span
          class="inline-block h-2 w-2 shrink-0 rounded-full"
          :class="isActive ? 'bg-status-success' : 'bg-muted-foreground/40'" />
        <span class="truncate">{{ groupName }}</span>
        <Badge
          v-if="!isActive"
          variant="outline"
          class="ml-1 font-normal text-muted-foreground">
          Inactive
        </Badge>
      </button>
    </TableCell>
    <TableCell class="w-28 text-right tabular-nums text-muted-foreground">€{{ totalDaily }}</TableCell>
    <TableCell class="w-24"></TableCell>
    <TableCell class="w-24 text-right">
      <Tooltip
        :disabled="!masterOverrideActive"
        content="Remove master campaign's manual budget">
        <InputNumber
          :model-value="ccampaign.daily_budget_manual"
          :disabled="masterOverrideActive"
          :min="0"
          class="h-8 text-right"
          @update:model-value="(value) => setCCampaignBudgetManual(ccampaign.id, value)" />
      </Tooltip>
    </TableCell>
    <TableCell
      v-if="showConstraints"
      class="w-20"></TableCell>
    <TableCell
      v-if="showConstraints"
      class="w-20"></TableCell>
    <TableCell class="w-16"></TableCell>
    <TableCell class="w-16"></TableCell>
    <TableCell class="w-16"></TableCell>
  </TableRow>
</template>
